import React, { useMemo } from "react";
import { useNavigate } from "react-router";
import { Avatar } from "@heroui/react";
import { Typography } from "../../../../components";
import { Creator } from "../../../../api/staff";
import { useTranslations } from "../../../../contexts/translation.context";
import { getCreatorLevelConfig } from "../constants";

interface CreatorInfoCellProps {
  creator: Creator;
}

/**
 * CreatorInfoCell Component
 *
 * Displays creator avatar, name and account type badge
 */
const CreatorInfoCell: React.FC<CreatorInfoCellProps> = ({ creator }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  const initials = useMemo(() => {
    if (creator.name) {
      const parts = creator.name.trim().split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      if (parts.length === 1 && parts[0]?.length > 0) {
        return parts[0][0].toUpperCase();
      }
    }

    return creator.email?.[0]?.toUpperCase() || "U";
  }, [creator.email, creator.name]);

  const levelConfig = useMemo(
    () => getCreatorLevelConfig(creator.lvl, t),
    [creator.lvl, t]
  );

  const handleClick = () => {
    navigate(`/users/creators/${creator.id}/balance`);
  };

  return (
    <div
      className="flex items-center gap-[12px] py-[8px] cursor-pointer hover:opacity-80 transition-opacity"
      onClick={handleClick}
    >
      <Avatar
        src={creator.avatar}
        showFallback
        name={initials}
        className="w-[40px] h-[40px] rounded-[12px] bg-gray-200 uppercase text-[14px] font-medium"
        classNames={{
          fallback: "bg-gray-200 text-gray-600"
        }}
      />

      <div className="flex flex-col gap-[4px]">
        <Typography variant="body3" className="!text-[#006FEE] text-[14px] font-semibold hover:underline">
          {creator.name}
        </Typography>

        <div className="flex items-center gap-[8px]">
          <span
            className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium capitalize w-fit"
            style={{
              backgroundColor: levelConfig.color,
              color: '#FFFFFF'
            }}
          >
            {levelConfig.label}
          </span>

          {/* Type Badge */}
          <span
            className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium capitalize w-fit"
            style={{
              backgroundColor: creator.type === 'agency' ? '#7828C8' + '20' : '#006FEE' + '20',
              color: creator.type === 'agency' ? '#7828C8' : '#006FEE'
            }}
          >
            {creator.type === 'agency' ? t('admin.creators.type.agency') : t('admin.creators.type.creator')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default CreatorInfoCell;
